// Tab Comparativas — año actual vs anterior, jurisdicción, juzgados, duración promedio.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { dashEjecComparativasJWT, dashEjecMapaCalorJWT } from '../../api/endpoints';
import MapaCalorSanJuan from './MapaCalorSanJuan';
import MapaCalorDetalleDialog from './MapaCalorDetalleDialog';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');

function VariacionBadge({ actual, anterior }) {
    if (!anterior) return null;
    const variacion = ((actual - anterior) / anterior) * 100;
    const Icon = variacion > 1 ? TrendingUpIcon : variacion < -1 ? TrendingDownIcon : TrendingFlatIcon;
    const color = variacion > 1 ? '#2e7d32' : variacion < -1 ? '#c62828' : '#666';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color, mt: 0.5 }}>
            <Icon fontSize="small" />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {variacion > 0 ? '+' : ''}{variacion.toFixed(1)}% vs año anterior
            </Typography>
        </Box>
    );
}

export default function TabComparativas({ desde, hasta, preset }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mapaData, setMapaData] = useState(null);
    const [deptoSeleccionado, setDeptoSeleccionado] = useState(null);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        dashEjecComparativasJWT(desde, hasta).then(r => { if (!cancel && r?.ok) setData(r.data); }).finally(() => { if (!cancel) setLoading(false); });
        // El mapa carga en paralelo (no bloquea el resto del tab); es snapshot.
        dashEjecMapaCalorJWT().then(r => { if (!cancel && r?.ok) setMapaData(r.data); });
        return () => { cancel = true; };
    }, [desde, hasta]);

    if (loading) {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[1, 2].map(i => <Grid key={i} size={{ xs: 12, md: 6 }}><Skeleton variant="rounded" height={140} sx={{ borderRadius: `${BR}px` }} /></Grid>)}
                </Grid>
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${BR}px` }} />
            </Box>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    const cmp = data.vsAnioAnterior || {};
    const dur = data.duracionPromedio || {};
    // Etiqueta del período actual para los cards interanuales.
    const PRESET_LABEL = { mes: 'Mes actual', trimestre: 'Últimos 3 meses', ano: 'Año actual', anoAnt: 'Año anterior', custom: 'Período seleccionado' };
    const periodoLabel = PRESET_LABEL[preset] || 'Período';
    const anioBase = desde ? new Date(desde + 'T00:00:00').getFullYear() : new Date().getFullYear();

    return (
        <Box>
            {/* Comparativa período seleccionado vs mismo período año anterior */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                        <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 700 }}>
                            Causas iniciadas — {periodoLabel}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1565c0', mt: 0.5 }}>
                            {fmtNum(cmp.iniciadosActual)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Mismo período {anioBase - 1}: {fmtNum(cmp.iniciadosAnterior)}
                        </Typography>
                        <VariacionBadge actual={cmp.iniciadosActual} anterior={cmp.iniciadosAnterior} />
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                        <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 700 }}>
                            Causas finalizadas — {periodoLabel}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', mt: 0.5 }}>
                            {fmtNum(cmp.finalizadosActual)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Mismo período {anioBase - 1}: {fmtNum(cmp.finalizadosAnterior)}
                        </Typography>
                        <VariacionBadge actual={cmp.finalizadosActual} anterior={cmp.finalizadosAnterior} />
                    </Paper>
                </Grid>
            </Grid>

            {/* Duración promedio */}
            {dur.cantConcluidas > 0 && (
                <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2,
                    background: 'linear-gradient(135deg, #6a1b9a 0%, #ab47bc 100%)', color: 'white' }}>
                    <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        ⏱️ Duración promedio de causas concluidas (último año)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 1 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                            {dur.dias.toFixed(0)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 400 }}>días</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, ml: 'auto' }}>
                            ≈ {(dur.dias / 30).toFixed(1)} meses · {(dur.dias / 365).toFixed(1)} años
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.5 }}>
                        Calculado sobre {fmtNum(dur.cantConcluidas)} causas concluidas/archivadas en los últimos 12 meses
                    </Typography>
                </Paper>
            )}

            {/* Por jurisdicción + Top juzgados */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Por jurisdicción
                        </Typography>
                        {(data.porJurisdiccion || []).length > 0 ? (
                            <BarChart
                                height={Math.max(280, (data.porJurisdiccion || []).length * 32)}
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: (data.porJurisdiccion || []).map(j => (j.jurisdiccion || '').slice(0, 35)) }]}
                                xAxis={[{}]}
                                series={[{ data: (data.porJurisdiccion || []).map(j => Number(j.cant || 0)), color: '#0277bd' }]}
                                margin={{ top: 10, right: 20, bottom: 30, left: 230 }}
                            />
                        ) : <Typography color="text.secondary">Sin datos</Typography>}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Top 10 juzgados con más causas
                        </Typography>
                        {(data.topJuzgados || []).length > 0 ? (
                            <BarChart
                                height={Math.max(280, (data.topJuzgados || []).length * 32)}
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: (data.topJuzgados || []).map(j => (j.juzgado || '').slice(0, 32)) }]}
                                xAxis={[{}]}
                                series={[{ data: (data.topJuzgados || []).map(j => Number(j.cant || 0)), color: '#7b1fa2' }]}
                                margin={{ top: 10, right: 20, bottom: 30, left: 220 }}
                            />
                        ) : <Typography color="text.secondary">Sin datos</Typography>}
                    </Paper>
                </Grid>
            </Grid>

            {/* Mapa de San Juan por departamento */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: PRIMARY }}>
                    Mapa de calor — Expedientes judiciales en trámite por departamento
                </Typography>
                <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 1.5 }}>
                    Cada expediente cuenta en el departamento donde se radica el juzgado. Click en un departamento para ver el detalle.
                </Typography>
                {mapaData === null ? (
                    <Skeleton variant="rounded" height={500} sx={{ borderRadius: `${BR}px` }} />
                ) : (
                    <MapaCalorSanJuan valores={mapaData} onClickDepto={(depto) => setDeptoSeleccionado(depto)} />
                )}
            </Paper>

            <MapaCalorDetalleDialog
                open={!!deptoSeleccionado}
                departamento={deptoSeleccionado}
                onClose={() => setDeptoSeleccionado(null)}
            />
        </Box>
    );
}
